import { useMutation } from "@apollo/client";
import Button from "components/base/Button";
import { Input, Select } from "components/base/Form";
import { Alert } from "components/base/Toast";
import OdooProductCategorySelector from "components/shared/OdooProductCategorySelector";
import { useState } from "react";
import formatShipmentProductName from "../inventory/warehouses/shipments/formatShipmentProductName";
import { DELETE_PRICE_LIST_ITEMS, SAVE_PRICE_LIST_ITEM } from "./graphql";
import PriceItemProductSelector from "./PriceItemProductSelector";

const PriceItemForm = ({
  company,
  priceListId,
  currency,
  onSaved,
  initialData = null,
  isInc = false,
}) => {
  const [product, setProduct] = useState(
    initialData ? initialData.product : null,
  );
  const [price, setPrice] = useState(initialData ? initialData.price : 0);
  const [state, setState] = useState(
    initialData ? initialData.state : "CONFIRMED",
  );
  const [useFixedMargin, setUseFixedMargin] = useState(
    initialData ? initialData.useFixedMargin : isInc ? true : false,
  );
  const [fixedMargin, setFixedMargin] = useState(
    initialData ? initialData.fixedMargin : isInc ? 30 : 0,
  );
  const [discountPercentage, setDiscountPercentage] = useState(
    initialData ? initialData.discountPercentage : 0,
  );
  const [createIncOdooProduct, setCreateIncOdooProduct] = useState(false);
  const [odooIncProductCategoryId, setOdooIncProductCategoryId] =
    useState(null);

  const [savePrice, savePriceRes] = useMutation(SAVE_PRICE_LIST_ITEM, {
    onCompleted() {
      Alert("success", "Saved.");
      onSaved();
    },
    onError(error) {
      Alert("error", error.message);
    },
    refetchQueries: ["FETCH_PRICE_LIST", "FETCH_ODOO_PRICE_LIST"],
    awaitRefetchQueries: true,
  });
  const [deleteProductSalesPrice, deleteProductSalesPriceRes] = useMutation(
    DELETE_PRICE_LIST_ITEMS,
    {
      onCompleted() {
        Alert("success", "Saved.");
        onSaved();
      },
      onError(error) {
        Alert("error", error.message);
      },
      refetchQueries: ["FETCH_PRICE_LIST"],
      awaitRefetchQueries: true,
    },
  );

  function saveHandler() {
    if (product) {
      if (createIncOdooProduct) {
        if (!odooIncProductCategoryId || odooIncProductCategoryId === "0") {
          Alert("error", "Please select a category for the new Odoo product.");
          return;
        }
      }

      savePrice({
        variables: {
          productId: product.id,
          priceListId,
          price,
          discountPercentage,
          useFixedMargin,
          fixedMargin,
          state,
          createIncOdooProduct,
          odooIncProductCategoryId,
        },
      });
    } else {
      Alert("error", "Please select a product.");
    }
  }

  return (
    <div>
      <div className="-mx-2">
        <table className="whitespace-nowrap">
          <tbody>
            <tr>
              <td>
                <label>Product({company}): </label>
              </td>
              <td className="w-full">
                <PriceItemProductSelector
                  className="text-sm"
                  company="AB"
                  valueView={
                    product ? (
                      <div>
                        [{product.number}] {product.name}
                      </div>
                    ) : null
                  }
                  optionView={(product) => formatShipmentProductName(product)}
                  onSelect={setProduct}
                />
              </td>
            </tr>

            <tr>
              <td>
                <label>Use Fixed Margin: </label>
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={useFixedMargin}
                  onChange={(e) => setUseFixedMargin(e.target.checked)}
                />
              </td>
            </tr>

            {useFixedMargin ? (
              <tr>
                <td>
                  <label htmlFor="">Fixed Margin: </label>
                </td>
                <td>
                  <div className="space-x-2">
                    <Input
                      className="text-center"
                      type="number"
                      value={fixedMargin}
                      onChange={(e) => setFixedMargin(e.target.value)}
                    />
                    <span>%</span>
                  </div>
                </td>
              </tr>
            ) : (
              <tr>
                <td>
                  <label htmlFor="">Price: </label>
                </td>
                <td>
                  <div className="space-x-2">
                    <Input
                      className="text-center"
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                    />
                    <span>{currency}</span>
                  </div>
                </td>
              </tr>
            )}

            <tr>
              <td>
                <label htmlFor="">Discount: </label>
              </td>
              <td>
                <div className="space-x-2">
                  <Input
                    className="text-center"
                    type="number"
                    value={discountPercentage}
                    onChange={(e) => setDiscountPercentage(e.target.value)}
                  />
                  <span>%</span>
                </div>
              </td>
            </tr>

            <tr>
              <td>
                <label htmlFor="">State: </label>
              </td>
              <td>
                <Select
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                >
                  <option value="PENDING">Pending</option>
                  <option value="CONFIRMED">Confirmed</option>
                </Select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {product && isInc && !product.odooIdInc && (
        <div className="py-4 my-4 border-y text-sm">
          <div>
            This product is not linked to an Inc Odoo product. You can create
            one now.
          </div>
          <div className="flex items-center space-x-3 mt-1">
            <label>Create product in Inc Odoo: </label>
            <input
              type="checkbox"
              checked={createIncOdooProduct}
              onChange={(e) => setCreateIncOdooProduct(e.target.checked)}
            />
          </div>

          {createIncOdooProduct && (
            <div className="mt-2 flex items-center space-x-3">
              <label>Choose Category: </label>
              <OdooProductCategorySelector
                instance="INC"
                value={odooIncProductCategoryId}
                onChange={setOdooIncProductCategoryId}
              />
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between space-x-8 mt-6">
        <Button
          primary
          onClick={saveHandler}
          loading={savePriceRes.loading}
          disabled={
            createIncOdooProduct &&
            (!odooIncProductCategoryId || odooIncProductCategoryId === "0")
          }
        >
          Save
        </Button>

        {initialData ? (
          <Button
            onClick={() => {
              if (
                window.confirm(
                  "Are you sure to delete this product in this price list? ",
                )
              ) {
                deleteProductSalesPrice({
                  variables: { priceListId, productIds: [product.id] },
                });
              }
            }}
            loading={deleteProductSalesPriceRes.loading}
            danger
          >
            Delete
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default PriceItemForm;
